/**
 * Les primitives d'écriture communes aux formulaires de /outils.
 *
 * Un état civil s'écrit de la même façon sur une déclaration de
 * non-condamnation, sur une attestation d'information au conjoint et sur les
 * neuf documents qui suivront. Ces fonctions vivent donc ici, et chaque
 * document se contente d'assembler ses phrases.
 *
 * Elles ne connaissent ni React, ni PDF, ni langue d'interface : un document
 * administratif français s'écrit en français, quelle que soit la langue dans
 * laquelle le lecteur a été guidé jusque-là.
 */

const MOIS = [
    "janvier", "février", "mars", "avril", "mai", "juin",
    "juillet", "août", "septembre", "octobre", "novembre", "décembre",
];

/**
 * Un montant écrit AU MILIEU D'UNE PHRASE : « 1 617,00 euros ».
 *
 * LE SYMBOLE € N'A AUCUNE LARGEUR D'AVANCE dans un PDF composé avec les polices
 * standard. jsPDF n'émet pas de tableau `/Widths`, le lecteur retombe sur les
 * métriques Helvetica d'Adobe, et celles-ci ne connaissent pas le glyphe
 * « Euro » : le caractère s'affiche, puis le mot suivant se pose PAR-DESSUS.
 * « d'un montant de 12 400,00 €pour la création » : c'est ce qu'ont imprimé la
 * réclamation de CFE et la mise en demeure pendant deux jours, sans que rien ne
 * le signale, parce qu'aucun harnais ne regardait le dessin.
 *
 * Le symbole reste donc permis là où il TERMINE une chaîne, en fin de cellule
 * ou de ligne, où rien ne peut le recouvrir. Partout ailleurs, c'est ce
 * libellé-ci, et il se lit mieux dans une phrase.
 *
 * Le séparateur de milliers rendu par `toLocaleString` en français est une
 * espace fine insécable (U+202F), elle aussi absente de l'encodage WinAnsi :
 * elle est ramenée à l'insécable ordinaire.
 */
export function eurosEnPhrase(valeur) {
    const chiffres = Number(valeur).toLocaleString("fr-FR", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
    });
    return `${chiffres.replace(/\u202f/g, "\u00a0")} euros`;
}

/** Trim, espaces multiples réduits, caractères de contrôle retirés. */
export function nettoyer(valeur) {
    if (typeof valeur !== "string") return "";
    return valeur
        .replace(/[\u0000-\u001f\u007f]/g, " ")
        .replace(/\s+/g, " ")
        .trim();
}

/**
 * Le nom de famille, en majuscules, comme l'écrit l'usage administratif.
 *
 * Les prénoms, eux, ne sont pas retouchés : capitaliser « McDonald » ou
 * « d'Angelo » à la machine abîme plus de noms qu'il n'en corrige.
 */
export function nomDeFamille(valeur) {
    return nettoyer(valeur).toLocaleUpperCase("fr-FR");
}

/**
 * Prénoms puis nom.
 *
 * Les modèles officiels décrivent le champ tantôt « prénom et nom », tantôt
 * « nom et prénom » : ils nomment ce qu'il faut fournir, pas l'ordre où
 * l'écrire. Nos documents gardent le même ordre partout, celui qui se lit.
 */
export function identite({ prenoms, nom }) {
    return [nettoyer(prenoms), nomDeFamille(nom)].filter(Boolean).join(" ");
}

/**
 * Une date en toutes lettres, avec le « 1er » que le français impose au
 * premier du mois et qu'aucun toLocaleDateString ne rend.
 *
 * L'entrée est une date ISO (AAAA-MM-JJ), lue en UTC : construite dans le
 * fuseau du navigateur, une date de naissance recule d'un jour à l'ouest de
 * Greenwich, et ce jour-là compte.
 */
export function dateEnToutesLettres(iso) {
    const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(String(iso || ""));
    if (!m) return "";
    const [, annee, mois, jour] = m;
    const n = Number(jour);
    const nomDuMois = MOIS[Number(mois) - 1];
    if (!nomDuMois || n < 1 || n > 31) return "";
    return `${n === 1 ? "1er" : n} ${nomDuMois} ${annee}`;
}

/** « Lyon », ou « Milan (Italie) » quand la naissance est hors de France. */
export function lieuDeNaissance({ villeDeNaissance, paysDeNaissance }) {
    const ville = nettoyer(villeDeNaissance);
    const pays = nettoyer(paysDeNaissance);
    if (!ville) return "";
    return pays ? `${ville} (${pays})` : ville;
}

/**
 * L'adresse, une ligne par ligne postale.
 *
 * C'est la forme qu'attend une enveloppe, et celle qu'un document met sous un
 * « à l'adresse suivante ». Découper après coup l'adresse d'une seule ligne
 * casserait sur une voie qui contient une virgule.
 */
export function lignesDAdresse({ voie, complement, codePostal, commune }) {
    const localite = [nettoyer(codePostal), nettoyer(commune)].filter(Boolean).join(" ");
    return [nettoyer(voie), nettoyer(complement), localite].filter(Boolean);
}

/** La même adresse au fil du texte, le complément inséré là où il se lit. */
export function adressePostale(adresse) {
    return lignesDAdresse(adresse).join(", ");
}

/** Nom du fichier téléchargé, sans accent ni espace. */
export function nomDuFichier(base, personne) {
    const suffixe = identite({ prenoms: personne?.prenoms, nom: personne?.nom })
        .normalize("NFD")
        .replace(/[\u0300-\u036f]/g, "")
        .replace(/[^a-zA-Z0-9]+/g, "-")
        .replace(/^-|-$/g, "")
        .toLowerCase();
    return `${base}${suffixe ? `-${suffixe}` : ""}.pdf`;
}

/**
 * Le document en texte brut, pour le copier-coller et pour les harnais.
 *
 * Il lit la forme en blocs que tous les documents partagent, celle que
 * l'aperçu et le PDF dessinent (voir components/Tools/DocumentPaper.jsx).
 */
export function documentEnTexte(document) {
    if (!document) return "";
    const morceaux = [document.titre, ""];
    for (const bloc of document.blocs) {
        if (bloc.type === "tableau") {
            // Les colonnes se séparent d'une tabulation : c'est ce qu'un
            // tableur comprend quand on colle le texte copié.
            morceaux.push(bloc.entetes.join("\t"));
            for (const ligne of bloc.lignes) morceaux.push(ligne.join("\t"));
            if (bloc.total) morceaux.push(bloc.total.join("\t"));
        } else if (bloc.type === "lignes") {
            morceaux.push(...bloc.lignes);
        } else if (bloc.type === "entete") {
            /* Le PDF pose les deux colonnes côte à côte ; le texte les met
               l'une sous l'autre, avec leur intitulé. Les oublier ici amputait
               le presse-papiers de l'émetteur et du client, c'est-à-dire de la
               moitié de ce qu'un devis identifie. */
            for (const colonne of bloc.colonnes) {
                if (colonne.libelle) morceaux.push(`${colonne.libelle} :`);
                morceaux.push(...colonne.lignes.filter(Boolean), "");
            }
            morceaux.pop();
        } else if (bloc.type === "signatures") {
            // Côte à côte sur la page, l'un sous l'autre dans le texte.
            for (const pave of bloc.colonnes) {
                if (pave.libelle) morceaux.push(pave.libelle);
                if (pave.nom) morceaux.push("", pave.nom);
                morceaux.push("");
            }
            morceaux.pop();
        } else if (bloc.type === "signature") {
            // Le nom sous le paraphe fait partie du document : l'oublier ici
            // faisait copier au presse-papiers un texte amputé de sa dernière
            // ligne, alors que le PDF, lui, la portait.
            if (bloc.libelle) morceaux.push(bloc.libelle);
            if (bloc.nom) morceaux.push("", bloc.nom);
        } else {
            morceaux.push(bloc.texte);
        }
        morceaux.push("");
    }
    return morceaux.join("\n").trimEnd();
}
